import httpClient from './httpClient';
import { LONG_ORCHESTRATOR_REQUEST_MS } from './apiTimeouts';

export interface VoiceAgentStartRequest {
  context_type?: string;
  interview_type?: string;
  candidate_name?: string;
  difficulty?: string;
  experience_focus?: string;
  mode?: string;
  max_questions?: number;
  token?: string;
}

export interface VoiceAgentStartRoundRequest {
  customInterviewId: string;
  roundNum: number;
  token: string;
}

export interface VoiceAgentBootstrap {
  sessionId: string;
  wsPath: string;
  firstQuestion?: string;
  maxQuestions?: number;
  contextType?: string;
}

export interface VoiceAgentStartResponse {
  success: boolean;
  session_id?: string;
  db_session_id?: string;
  ws_path?: string;
  first_question?: string;
  max_questions?: number;
  context_type?: string;
  message?: string;
  error?: string;
}

export interface VoiceAgentStatusResponse {
  enabled: boolean;
  deepgram_configured: boolean;
}

export interface VoiceAgentSessionMetrics {
  question_count: number;
  max_questions: number;
  warning_count: number;
  max_warnings: number;
  remaining_questions?: number;
  should_wrap_up?: boolean;
  should_end_now?: boolean;
}

export interface VoiceAgentEndResponse {
  success: boolean;
  message?: string;
  status?: 'completed' | 'abandoned';
  feedback?: Record<string, unknown>;
  conversation_history?: Array<{ role: string; content: string }>;
  total_questions?: number;
  reason?: string;
}

export const voiceAgentApi = {
  async getStatus(): Promise<VoiceAgentStatusResponse | null> {
    try {
      const response = await httpClient.get('/voice-agent/status');
      return response.data;
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  async startInterview(data: VoiceAgentStartRequest): Promise<VoiceAgentStartResponse> {
    try {
      const response = await httpClient.post('/voice-agent/start-interview', {
        context_type: data.context_type ?? 'practice',
        interview_type: data.interview_type ?? 'general',
        candidate_name: data.candidate_name,
        difficulty: data.difficulty ?? 'medium',
        experience_focus: data.experience_focus ?? 'varied',
        mode: data.mode ?? 'professional',
        max_questions: data.max_questions,
        token: data.token,
      }, { timeout: LONG_ORCHESTRATOR_REQUEST_MS });
      return response.data;
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string' && detail.trim()) {
        throw new Error(detail);
      }
      throw error;
    }
  },

  async startRound(data: VoiceAgentStartRoundRequest): Promise<VoiceAgentStartResponse> {
    const response = await httpClient.post('/voice-agent/start-round', {
      custom_interview_id: data.customInterviewId,
      round_num: data.roundNum,
      token: data.token,
    }, { timeout: LONG_ORCHESTRATOR_REQUEST_MS });
    return response.data;
  },

  async endInterview(sessionId: string, reason?: string): Promise<VoiceAgentEndResponse> {
    const response = await httpClient.post(
      '/voice-agent/end-interview',
      {
        session_id: sessionId,
        reason: reason ?? 'user_request',
      },
      { timeout: LONG_ORCHESTRATOR_REQUEST_MS }
    );
    return response.data;
  },
};

export default voiceAgentApi;
